import { DEFAULT_IMAGE, DEFAULT_PROFILE_IMAGE } from "./default-static-urls";
import { isOutschoolUrl } from "./url-utils";
import { captureError } from "@outschool/platform";

type ImageFormats = "jpg" | "jpeg" | "webp";

type ResizeOptions = {
  height?: number;
  h?: number;
  width?: number;
  w?: number;
  fit?: string;
};

export function transformedImageUrl(
  url?: string | null,
  options?: ResizeOptions,
  format: ImageFormats = "jpg",
) {
  if (!url) {
    return osImageUrl(DEFAULT_IMAGE, options, format);
  }

  if (!isOutschoolUrl(url)) {
    captureError(
      new Error(`Transformation on this url '${url}' is not supported`),
      {
        extra: {
          options: JSON.stringify(options),
          url,
        },
      },
    );

    // just return the url if it's not an outschool url
    // sometimes we have static2.outschool domain urls but those shouldn't go through transformation
    return url;
  }

  return osImageUrl(url, options, format);
}

export function osImageUrl(
  url?: string | null,
  options?: ResizeOptions,
  format: ImageFormats = "jpg",
  watermark?: boolean,
) {
  if (!url) {
    // This is the default "missing photo" image we show
    url = DEFAULT_IMAGE;
  }

  /*
    /images is not an actual path to s3 bucket, but something that 
    is used to trigger transformation services from media-app.

    if original was
    https://cdn.outschool.com/<s3path>
    then we want this to be
    https://cdn.outschool.com/images/<s3path>
  */
  let originalUrl: URL;
  try {
    originalUrl = new URL(url); // parses host, pathname, etc.
  } catch (error) {
    // URL is invalid
    console.warn(`Invalid URL provided to transformation: '${url}'`, error);
    return "invalid url"; // Return empty url --> will indicate that there is a problem so we can actually fix it
  }
  originalUrl.pathname = `/images${originalUrl.pathname}`;
  const osCdnUrl = originalUrl.toString();

  const resize =
    options && (options.width || options.height || options.w || options.h)
      ? resizeParams(options)
      : [];

  const transformParams = [
    ...resize,
    // strip:true removes metadata information from the photo
    "quality=80",
    "strip=true",
  ];

  if (format && format === "webp") {
    transformParams.push(`outputFormat=${format}`);
  }

  if (watermark) {
    transformParams.push("watermark=true");
  }

  return `${osCdnUrl}?${transformParams.join("&")}`;
}

function resizeParams(options: ResizeOptions = {}) {
  const unroundedHeight = options.height ?? options.h;
  const unroundedWidth = options.width ?? options.w;
  const height = unroundedHeight ? Math.round(unroundedHeight) : false;
  const width = unroundedWidth ? Math.round(unroundedWidth) : false;

  const { w: _w, h: _h, ...optionsWithoutDimensions } = options;
  return Object.entries({
    ...optionsWithoutDimensions,
    width,
    height,
  })
    .map(([key, val]) => val && `${key}=${val}`)
    .filter(x => x)
    .sort();
}

export function videoThumbnailUrl(
  url?: string | null,
  options?: ResizeOptions,
  _format: ImageFormats = "jpg",
) {
  if (!url || !isOutschoolUrl(url)) {
    captureError(
      new Error(
        `Video Thumbnail Transformation on this url ${url} is not supported`,
      ),
    );
    return osImageUrl(DEFAULT_IMAGE, options, "webp", true);
  }

  return osImageUrl(url, options, "webp", true);
}

// Currently using: 125 x 125 full size, 60x60 in featured cards and header (shown as 30x30)
export function headshotImageUrl(
  url: string | undefined | null,
  size: number = 125,
) {
  return url
    ? transformedImageUrl(url, { w: size, h: size, fit: "crop" }, "webp")
    : transformedImageUrl(
        DEFAULT_PROFILE_IMAGE,
        { w: size, h: size, fit: "crop" },
        "webp",
      );
}

export const HOMEPAGE_PREVIEW_IMAGE =
  "https://cdn.outschool.com/outschool-filestack-migration/amvs1rulSqCuHsw3wm1a_temp.png";
